import { DragSettings } from './Models/DragSettings';
import { DropZoneSettings } from './Models/DropZoneSettings';
import { DragContext } from './Models/DragContext';

/**
 * The DragHandler class provides functionality for enabling drag and drop operations on HTML elements.
 */
export class DragHandler {
    private settings: Map<HTMLElement, DragSettings> = new Map();
    private snapX: number = null;
    private snapY: number = null;
    private width: number = null;
    private height: number = null;
    private display: string = null;
    private clone: HTMLElement = null;
    private lastGridIndexX: number = null;
    private draggable: HTMLElement = null;
    private dropTarget: HTMLElement = null;
    private dropSettings: DropZoneSettings = null;
    private dragStartHandler: any;
    private dragHandler: any;
    private dragEndHandler: any;
    private snapXOffset: number = null;
    /**
     * Initializes the drag functionality for the specified element with the given settings.
     * @param element The HTML element to enable drag on.
     * @param settings The drag settings for the element.
     */
    constructor() {
        this.dragStartHandler = this.onDragStart.bind(this);
        this.dragHandler = this.onDrag.bind(this);
        this.dragEndHandler = this.onDragEnd.bind(this);
    }

    /**
     * Initializes the drag functionality for the specified element with the given settings.
     * @param element The HTML element to enable drag on.
     * @param settings The drag settings for the element.
     */
    public init(element: HTMLElement, settings: DragSettings): void {
        // Add javascript event listeners.
        element.addEventListener('dragstart', this.dragStartHandler);
        element.addEventListener('drag', this.dragHandler);
        element.addEventListener('dragend', this.dragEndHandler);

        if (settings.isDisabled === true) {
            element.setAttribute('draggable', 'false');
        } else {
            element.setAttribute('draggable', 'true');
        }

        this.settings.set(element, settings);
    }

    /**
     * Retrieves the drag context of the current element.
     * @returns The drag context.
     */
    public getElementContext(): DragContext {
        const settings = this.settings.get(this.draggable);
        let colXUnit: number = null;
        if (this.dropSettings !== null) {
            colXUnit = this.lastGridIndexX * this.dropSettings.colXUnit + this.dropSettings.xOffset;
        }

        return new DragContext(settings.id, colXUnit);
    }

    /**
     * Retrieves the type of the element.
     * @returns The type of the element.
     */
    public getElementType(): string {
        const settings = this.settings.get(this.draggable);

        return settings.type;
    }

    /**
     * Updates the drag settings for the specified element.
     * @param element The HTML element to update the settings for.
     * @param settings The new drag settings for the element.
     */
    public updateSettings(element: HTMLElement, settings: DragSettings): void {
        this.settings.set(element, settings);

        if (settings.isDisabled === true) {
            element.setAttribute('draggable', 'false');
        } else {
            element.setAttribute('draggable', 'true');
        }
    }

    /**
     * Disposes the specified HTML element.
     * @param element The HTML element to dispose.
     */
    public dispose(element: HTMLElement): void {
        if (element === null){
            return;
        }
        
        // Remove javascript event listeners.
        element.removeEventListener('dragstart', this.dragStartHandler);
        element.removeEventListener('drag', this.dragHandler);
        // Don't remove 'dragend' manually, because dragend is called after the drop event.

        this.settings.delete(element);
    }

    /**
     * Handles the event when the draggable element enters a drop target.
     * @param dropTargetElement The HTML element of the drop target.
     * @param settings The drop zone settings.
     */
    public onDropTargetEnter(dropTargetElement: HTMLElement, settings: DropZoneSettings): void {
        this.dropTarget = dropTargetElement;
        this.dropSettings = settings;
    }

    /**
     * Handles the event when the draggable element leaves a drop target.
     * @param dropTargetElement The HTML element of the drop target.
     */
    public onDropTargetLeave(dropTargetElement: HTMLElement): void {
        if (dropTargetElement !== this.dropTarget) {
            return;
        }

        this.dropTarget = null;
        this.dropSettings = null;
        this.snapX = null;
        this.snapY = null;
    }

    /**
     * Handles the event when the draggable element starts to be dragged.
     * @param event The drag event.
     */
    public onDragStart(event: DragEvent): void {
        // Remove default ghost
        event.dataTransfer.setDragImage(new Image(), 0, 0);

        // Get our current element.
        const element = event.target as HTMLElement;
        this.draggable = element;

        // Decide what we're going to drag. A clone of our current element, or a clone of the draggable-content element.
        let elementToDrag = this.cloneElementToDrag(element);

        // Set correct position.
        elementToDrag.style.left = event.pageX.toString() + 'px';
        elementToDrag.style.top = event.pageY.toString() + 'px';

        // Apply settings.
        const settings = this.settings.get(element);

        // Because we update the element, in the ondragstart event, we need to wrap it in a timeout so dragend won't be called immediately.
        setTimeout(function () {
            document.body.appendChild(elementToDrag);

            this.width = elementToDrag.clientWidth;
            this.height = elementToDrag.clientHeight;

            // Hide the original element if settings.removeGhostWhileDragging is true.
            if (settings.removeGhostWhileDragging === true) {
                this.display = element.style.display;
                element.style.display = 'none';
            }

            this.clone = elementToDrag;
        }.bind(this), 0);
    }

    /**
     * Handles the event when the draggable element is being dragged.
     * @param event The drag event.
     */
    public onDrag(event: DragEvent): void {
        if (this.clone === null) {
            return;
        }

        // Make sure we have the correct parent.
        if (this.dropTarget !== null && this.clone.parentNode !== this.dropTarget) {
            this.setCloneSize();
            this.dropTarget.appendChild(this.clone);
        }
        if (this.dropTarget === null && this.clone.parentNode !== document.body) {
            this.setCloneSize();
            document.body.appendChild(this.clone);
        }

        // Calculate snap values.
        if (this.dropSettings !== null && this.dropTarget !== null) {
            if (this.dropSettings.noOfColsX !== null) {
                const remainder = this.dropSettings.colXUnitTotal % this.dropSettings.colXUnit;
                const remainderFactor = remainder / this.dropSettings.colXUnitTotal;
                this.snapX = (this.dropTarget.clientWidth - (this.dropTarget.clientWidth * remainderFactor)) / this.dropSettings.noOfColsX;
                const factor = this.dropSettings.xOffset / this.dropSettings.colXUnit;
                this.snapXOffset = factor * this.snapX;
            }

            if (this.dropSettings.noOfColsY !== null) {
                this.snapY = this.dropTarget.clientHeight / this.dropSettings.noOfColsY;
            }
        }

        // Set correct position.
        let x = event.pageX;
        let y = event.pageY;

        // If outside the page, then ignore the update.
        if (x == 0 && y == 0) {
            return;
        }

        if (this.dropTarget !== null) {
            const scroll = this.cumulativeScroll(this.dropTarget);
            const offset = this.cumulativeOffset(this.dropTarget);
            x -= offset.left - scroll.x;
            y -= offset.top - scroll.y;
        }

        let leftSnap = this.snap(x, this.snapX, this.snapXOffset); // pass the offset here
        let topSnap = this.snap(y, this.snapY);
        let left = leftSnap.position;
        let top = topSnap.position;

        // clamp the position to the drop target.
        if (this.dropTarget !== null) {
            left = this.clamp(left, 0, this.dropTarget.clientWidth - this.snapXOffset - this.clone.offsetWidth);
            leftSnap = this.snap(left, this.snapX, this.snapXOffset); // re-snap after clamping for determining correct lastGridIndexX
            top = this.clamp(top, 0, this.dropTarget.clientHeight - this.clone.offsetHeight);
        }
                
        this.lastGridIndexX = leftSnap.gridIndex;

        this.clone.style.left = left.toString() + 'px';
        this.clone.style.top = top.toString() + 'px';
    }

    /**
     * Handles the event when the draggable element is being dragged.
     * @param event The drag event.
     */
    public onDragEnd(event: DragEvent): void {
        // remove clone
        if (this.clone !== null) {
            this.clone.parentNode.removeChild(this.clone);
            this.clone = null;
        }

        const element = event.target as HTMLElement;
        element.classList.remove('draggable--ghost');
        if (element.style.display === "none") {
            if (this.display === null) {
                element.style.display = null;
            } else {
                element.style.display = this.display;
                this.display = null;
            }

        }
        element.classList.add('draggable');
      
        // Cleanup
        this.draggable = null;
        this.dropTarget = null;
        this.dropSettings = null;
        this.snapX = null;
        this.snapY = null;
    }

    /**
    * Sets the size of the clone element.
    */
    private setCloneSize(): void {
        const settings = this.settings.get(this.draggable);

        let width = this.width;
        let height = this.height;

        if (this.dropSettings !== null && this.dropTarget !== null) {
            if (this.dropSettings.noOfColsX !== null && this.dropSettings.colXUnit !== null && settings.numberOfUnitsX !== null) {
                const colWidth = this.dropTarget.clientWidth / this.dropSettings.noOfColsX;
                width = colWidth * (settings.numberOfUnitsX / this.dropSettings.colXUnit);
            } else if (this.dropSettings.noOfColsX !== null) {
                width = this.dropTarget.clientWidth / this.dropSettings.noOfColsX;
            }
            else {
                width = this.dropTarget.clientWidth;
            }

            if (this.dropSettings.noOfColsY !== null && this.dropSettings.colYUnit !== null && settings.numberOfUnitsY !== null) {
                const colHeight = this.dropTarget.clientHeight / this.dropSettings.noOfColsY;
                height = colHeight * (settings.numberOfUnitsY / this.dropSettings.colYUnit);
            } else if (this.dropSettings.noOfColsY !== null) {
                height = this.dropTarget.clientHeight / this.dropSettings.noOfColsY;
            } else {
                height = this.dropTarget.clientHeight;
            }
        }

        this.clone.style.width = (width).toString() + 'px';
        this.clone.style.height = (height).toString() + 'px';
    }

    /**
     * Calculates the cumulative offset of the specified HTML element.
     * @param element The HTML element to calculate the offset for.
     * @returns The cumulative offset of the element.
     */
    private cumulativeOffset(element: HTMLElement): any {
        let top = 0, left = 0;
        do {
            top += element.offsetTop || 0;
            left += element.offsetLeft || 0;
            element = element.offsetParent as HTMLElement;
        } while (element);

        return {
            top: top,
            left: left
        };
    }

    /**
     * Calculates the cumulative scroll of the specified HTML element.
     * @param element The HTML element to calculate the scroll for.
     * @returns The cumulative scroll of the element.
     */
    private cumulativeScroll(element: HTMLElement): any {
        let y = 0, x = 0;
        do {
            y += element.scrollTop || 0;
            x += element.scrollLeft || 0;
            element = element.parentElement as HTMLElement;
        }
        while (element);

        return {
            x: x,
            y: y
        }
    }

    /**
     * Snaps the current value to the specified snap value.
     * @param currentValue The current value to snap.
     * @param snapTo The snap value to use.
     * @param offset The offset to apply.
     * @returns An object containing the grid index and the snapped position.
     */
    private snap(currentValue: number, snapTo: number, snapXOffset: number = 0): any {
        if (snapTo === null || snapTo === 0) {
            return {
                gridIndex: null,
                position: currentValue + snapXOffset // Apply offset when snapTo is null or 0
            };
        }

        const gridColumn = Math.floor(currentValue / snapTo);
        const position = gridColumn * snapTo + snapXOffset; // Apply offset to snapped position

        return {
            gridIndex: gridColumn,
            position: position
        };
    }

    /**
     * Clones the specified HTML element to be dragged.
     * @param element The HTML element to clone.
     * @returns The cloned HTML element.
     */
    private cloneElementToDrag(element: HTMLElement): HTMLElement {
        // Remove the draggable class, so we don't get a clone of our clone.
        element.classList.remove('draggable');

        // Decide what we're going to drag. A clone of our current element, or a clone of the draggable-content element.
        let elementToDrag = null;
        const draggableContent = element.querySelector('.dragged-content');
        if (draggableContent != null) {
            const numberOfChildren = draggableContent.children.length;
            if (numberOfChildren == 1) {
                // only one child
                elementToDrag = draggableContent.children[0].cloneNode(true);
            }
            else if (numberOfChildren > 1) {
                elementToDrag = draggableContent.cloneNode(true);
                elementToDrag.style.removeProperty("display");
            }
        }

        if (elementToDrag === null) {
            elementToDrag = element.cloneNode(true);

            // because our element is absolute, force the clone to have the same dimensions.
            elementToDrag.style.width = (element.clientWidth).toString() + 'px';
            elementToDrag.style.height = (element.clientHeight).toString() + 'px';
        }

        // Make sure the correct class is set.
        elementToDrag.classList.add('draggable--dragging');
        element.classList.add('draggable--ghost');

        return elementToDrag;
    }

    /**
     * Clamps the specified value between the minimum and maximum values.
     * @param value The value to clamp.
     * @param min The minimum value.
     * @param max The maximum value.
     * @returns The clamped value.
     */
    private clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max);
    }
}

window['DragHandler'] = new DragHandler();
